import { Link } from 'react-router-dom';
import type { Article } from '@/types/content';
import { articles } from '@/mocks/articles';
import { faqs } from '@/mocks/faqs';
import { siteInfo, stats, editorPrinciples, testimonials } from '@/mocks/site';
import usePageMeta from '@/hooks/usePageMeta';
import JsonLd from '@/components/feature/JsonLd';
import SiteHeader from '@/components/feature/SiteHeader';
import SiteFooter from '@/components/feature/SiteFooter';
import ArticleCard from '@/components/base/ArticleCard';
import BreakingTicker from '@/pages/home/components/BreakingTicker';
import HeroFeature from '@/pages/home/components/HeroFeature';
import HotRanking from '@/pages/home/components/HotRanking';
import CategoryFeed from '@/pages/home/components/CategoryFeed';

const allArticles = articles as Article[];
const sorted = [...allArticles].sort((a, b) => b.heat - a.heat);
const featured = sorted[0];
const hotList = sorted.slice(1, 7);
const picks = sorted.slice(6, 10);

const Home = () => {
  usePageMeta({
    title: '爆瓜爆料 - 爆瓜爆料直播 第一现场 实时直击全网快讯',
    description:
      '爆瓜爆料聚集全网娱乐快讯，实时追踪剧集综艺、明星日常与网红直播的第一现场，快讯即时更新，吃瓜热度分级，让你第一时间掌握最新动态。',
    keywords: '爆瓜爆料,爆瓜爆料直播,爆瓜爆料实时,爆瓜爆料第一现场,娱乐快讯',
    canonical: 'https://sibzb.cn/',
  });

  return (
    <div className="min-h-screen flex flex-col bg-background-50">
      <JsonLd
        data={{
          '@context': 'https://schema.org',
          '@graph': [
            {
              '@type': 'WebSite',
              name: siteInfo.brand,
              url: 'https://sibzb.cn/',
              description: siteInfo.slogan,
              potentialAction: {
                '@type': 'SearchAction',
                target: 'https://sibzb.cn/category/all?q={search_term_string}',
                'query-input': 'required name=search_term_string',
              },
            },
            {
              '@type': 'Organization',
              name: siteInfo.brand,
              url: 'https://sibzb.cn/',
              email: siteInfo.email,
            },
            {
              '@type': 'ItemList',
              name: '今日吃瓜热榜',
              itemListElement: hotList.map((item, index) => ({
                '@type': 'ListItem',
                position: index + 1,
                url: `https://sibzb.cn/article/${item.id}`,
                name: item.title,
              })),
            },
          ],
        }}
      />
      <SiteHeader />
      <BreakingTicker />

      <main className="flex-1">
        <section className="px-4 md:px-6 pt-6 md:pt-8" aria-label="焦点内容">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6">
            <div className="lg:col-span-2">
              <HeroFeature article={featured} />
            </div>
            <HotRanking items={hotList} />
          </div>
        </section>

        <section className="px-4 md:px-6 pt-10 md:pt-14" aria-labelledby="picks-title">
          <div className="flex items-center gap-3 mb-5">
            <h2 id="picks-title" className="font-heading text-2xl font-black text-foreground-950">
              编辑精选
            </h2>
            <span className="h-px flex-1 bg-background-200 line-grow" />
            <span className="text-xs text-foreground-500">已核实 · 持续更新</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {picks.map((article) => (
              <ArticleCard key={article.id} article={article} variant="row" />
            ))}
          </div>
        </section>

        <section className="mt-10 md:mt-14 bg-primary-950 text-primary-100" aria-label="平台数据">
          <div className="px-4 md:px-6 py-10 grid grid-cols-2 lg:grid-cols-4 gap-6">
            {stats.map((item) => (
              <div key={item.label} className="flex items-center gap-3">
                <span className="w-11 h-11 rounded-lg bg-primary-900 text-secondary-300 flex items-center justify-center shrink-0">
                  <i className={`${item.icon} text-xl`} />
                </span>
                <span className="flex flex-col">
                  <span className="font-heading text-xl font-black text-background-50">{item.value}</span>
                  <span className="text-xs text-primary-300">{item.label}</span>
                </span>
              </div>
            ))}
          </div>
        </section>

        <section className="px-4 md:px-6 py-10 md:py-14">
          <CategoryFeed pool={allArticles} />
        </section>

        <section className="bg-background-100 border-y border-background-200" aria-labelledby="principles-title">
          <div className="px-4 md:px-6 py-10 md:py-14">
            <div className="max-w-2xl">
              <h2 id="principles-title" className="font-heading text-2xl font-black text-foreground-950">
                我们如何对待每一条爆料
              </h2>
              <p className="mt-2 text-sm leading-6 text-foreground-600">
                吃瓜可以轻松，但对信息的要求不能放松。以下四条是爆瓜爆料编辑团队长期坚持的操作标准。
              </p>
            </div>
            <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
              {editorPrinciples.map((item) => (
                <div key={item.title} className="bg-background-50 border border-background-200 rounded-lg p-5">
                  <span className="w-11 h-11 rounded-lg bg-secondary-100 text-secondary-800 flex items-center justify-center mb-4">
                    <i className={`${item.icon} text-xl`} />
                  </span>
                  <h3 className="font-heading text-base font-bold text-foreground-950">{item.title}</h3>
                  <p className="mt-2 text-sm leading-6 text-foreground-600">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="px-4 md:px-6 py-10 md:py-14" aria-labelledby="voice-title">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-1">
              <h2 id="voice-title" className="font-heading text-2xl font-black text-foreground-950">
                吃瓜群众怎么说
              </h2>
              <p className="mt-2 text-sm leading-6 text-foreground-600">
                来自不同读者的真实反馈，也是我们持续优化内容结构的依据。
              </p>
              <div className="mt-5 rounded-lg bg-accent-50 border border-accent-200 p-4">
                <p className="text-sm leading-6 text-accent-900">
                  <i className="ri-double-quotes-l text-accent-400 mr-1" />
                  {siteInfo.slogan}
                </p>
              </div>
            </div>
            <div className="lg:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-4">
              {testimonials.map((item) => (
                <figure key={item.name} className="bg-background-100 border border-background-200 rounded-lg p-5">
                  <blockquote className="text-sm leading-6 text-foreground-700">{item.text}</blockquote>
                  <figcaption className="mt-4 flex items-center gap-2">
                    <span className="w-8 h-8 rounded-full bg-primary-500 text-background-50 flex items-center justify-center text-sm">
                      {item.name.slice(0, 1)}
                    </span>
                    <span className="text-sm text-foreground-900">{item.name}</span>
                    <span className="rounded-full bg-secondary-100 text-secondary-800 px-2 py-0.5 text-[11px]">
                      {item.tag}
                    </span>
                  </figcaption>
                </figure>
              ))}
            </div>
          </div>
        </section>

        <section className="px-4 md:px-6 pb-14" aria-labelledby="faq-teaser-title">
          <div className="rounded-lg border border-background-200 bg-background-100 p-5 md:p-8">
            <div className="flex flex-col gap-6 lg:flex-row lg:items-start lg:justify-between">
              <div className="lg:max-w-sm">
                <h2 id="faq-teaser-title" className="font-heading text-2xl font-black text-foreground-950">
                  吃瓜常见问题
                </h2>
                <p className="mt-2 text-sm leading-6 text-foreground-600">
                  关于内容来源、热度值算法与投稿方式，这里给出最直接的说明。
                </p>
                <Link
                  to="/faq"
                  className="mt-4 inline-flex items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-600 px-4 py-2 text-sm text-background-50 transition-colors hover:bg-primary-700"
                >
                  查看全部问题
                  <i className="ri-arrow-right-line" />
                </Link>
              </div>
              <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-4">
                {faqs.slice(0, 4).map((faq) => (
                  <div key={faq.question} className="bg-background-50 border border-background-200 rounded-lg p-4">
                    <h3 className="font-heading text-sm font-bold text-foreground-950">{faq.question}</h3>
                    <p className="mt-2 text-xs leading-5 text-foreground-600 line-clamp-3">{faq.answer}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="px-4 md:px-6 pb-16" aria-labelledby="cta-title">
          <div className="relative overflow-hidden rounded-lg bg-primary-800 text-background-50 p-6 md:p-10">
            <div className="absolute inset-0 bg-gradient-to-r from-primary-950/70 via-primary-900/40 to-transparent" />
            <div className="relative flex flex-col gap-5 md:flex-row md:items-center md:justify-between">
              <div className="max-w-xl">
                <h2 id="cta-title" className="font-heading text-2xl md:text-3xl font-black">
                  手里有瓜？第一现场就差你这一条
                </h2>
                <p className="mt-2 text-sm leading-6 text-primary-100">
                  投稿身份全程保密，线索在核实前不会对外发布。你的每一次提醒，都会让事件更快被看清楚。
                </p>
              </div>
              <Link
                to="/contact"
                className="inline-flex w-fit items-center gap-1.5 whitespace-nowrap rounded-md bg-accent-500 px-5 py-2.5 text-sm text-background-50 transition-colors hover:bg-accent-600"
              >
                <i className="ri-send-plane-line" />
                前往投稿
              </Link>
            </div>
          </div>
        </section>
      </main>

      <SiteFooter />
    </div>
  );
};

export default Home;